import { Box, type SxProps } from "@mui/material";
import { type Theme } from "@mui/material/styles";
import { CbhIcon, type CbhIconProps, mergeSxProps } from "@src/appV2/redesign/system/ui-components";

import { type ExternalAccountLite } from "../api/schema";

export interface ExternalAccountLogoProps {
  externalAccount: ExternalAccountLite;
  /** Default: "large" / "medium", depending on the external account */
  iconSize?: CbhIconProps["size"];
  grayscale?: boolean;
  sx?: SxProps<Theme>;
}

function getIconType(externalAccount: ExternalAccountLite): CbhIconProps["type"] {
  // FIXME: Automatically disabled during ESLint config update
  switch (externalAccount.providerName) {
    case "MasterCard": {
      return "mastercard-colored";
    }

    case "Visa": {
      return "visa-colored";
    }

    case "American Express": {
      return "american-express-colored";
    }

    default: {
      return externalAccount.type === "card" ? "credit-card" : "bank";
    }
  }
}

function getIconSize(externalAccount: ExternalAccountLite): Required<CbhIconProps["size"]> {
  // FIXME: Automatically disabled during ESLint config update
  switch (externalAccount.providerName) {
    case "MasterCard":
    case "Visa":
    case "American Express": {
      return "large";
    }

    default: {
      return "medium";
    }
  }
}

export function ExternalAccountLogo(props: ExternalAccountLogoProps) {
  const { externalAccount, grayscale, sx, iconSize = getIconSize(externalAccount) } = props;
  const iconType = getIconType(externalAccount);

  return (
    <Box
      data-testid="external-account-logo-container"
      sx={mergeSxProps(
        (theme) => ({
          // 32x32px box, center the icon within it
          // this is because some icons are medium and some are large size
          // but we want both to take the same physical space so items look consistent
          width: theme.spacing(8),
          height: theme.spacing(8),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          filter: grayscale ? "grayscale(100%)" : undefined,
        }),
        sx
      )}
    >
      <CbhIcon data-testid={`${iconType}-${iconSize}`} type={iconType} size={iconSize} />
    </Box>
  );
}
